import { memo, useEffect } from 'react'
import { useActions } from '../../../../providers'
import { useActiveChatId, useChatMessages, useLastFocusedWindow } from '../../../../store'

/**
 * Inserts the last assistant message (or its last code block, on Shift+Tab)
 * on plain Tab. Fires alongside `WindowContextTabToggleHandler` — the two
 * are mutually exclusive by their own modifier checks (that handler requires
 * Alt/Ctrl, this one requires their absence), so neither needs to know
 * about the other.
 */
export const TabTypingAssistanceHandler = memo(() => {
  const { handleTyping } = useActions()
  const lastFocusedWindow = useLastFocusedWindow()
  const activeChatId = useActiveChatId()
  const messages = useChatMessages(activeChatId)

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') {
        return
      }

      // Handle typing assistance: plain Tab or Shift+Tab
      if (!event.altKey && !event.ctrlKey && !event.metaKey) {
        event.preventDefault()
        if (messages.length > 0 && messages[messages.length - 1].role === 'assistant') {
          const shouldInsertCode = event.shiftKey
          const message = messages[messages.length - 1]
          // we want the code from the last code block
          const code =
            typeof message.content === 'string'
              ? message.content
                  .match(/```([\s\S]*?)```/g)
                  ?.pop()
                  ?.replace(/```.*\n|\n```/g, '')
                  .trim()
              : undefined
          if (!shouldInsertCode || code)
            handleTyping?.(
              shouldInsertCode ? { ...message, content: code || message.content } : message,
              lastFocusedWindow,
              { source: 'keyboard_shortcut' },
            )
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [lastFocusedWindow, messages, handleTyping])

  return null
})
TabTypingAssistanceHandler.displayName = 'TabTypingAssistanceHandler'
